import {ActionCreator, createAction} from '@packages/utilities/create-action';
import {Component} from '@packages/systems/wfdl';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {reportBug} from '@packages/utilities/reporter';
import {emptyObject} from '@packages/utilities/fp/utils';
import {transformNodeHoveredPayload} from './transformNodeHoveredPayload';
import type {NodeHoveredPayload} from './types';

export const getNodeHoveredActionCreator = (
  components: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
) => {
  return _nodeHoveredActionCreatorFactory(
    components,
    focusedSymbolNativeIdPath
  );
};

type AnyStateType = any;

const DUMMY_ACTION: any = emptyObject;

export const getNodeHoveredActionCreatorFromState = (
  state: AnyStateType
): ActionCreator<'NODE_HOVERED', NodeHoveredPayload> => {
  const components = state?.DesignerStore?.components;
  const focusedSymbolNativeIdPath =
    state?.UiNodeStore?.focusedSymbolNativeIdPath;

  if (!components || !focusedSymbolNativeIdPath) {
    reportBug(
      'DesignerStateMissingArgumentInNodeHoveredActionCreator',
      'Attempted to create a NODE_HOVERED action creator from state in `getNodeHoveredActionCreatorFromState`, but provided state had missing dependencies!',
      {
        team: 'dxp',
        ticket: 'https://webflow.atlassian.net/browse/DESXP-6522',
        data: {
          components,
          focusedSymbolNativeIdPath,
        },
      }
    );

    // If we reported the above bug, return an action creator that returns
    // an empty object as an action so that it is ignored by any reducer that may
    // be expecting NODE_HOVERED.
    return () => DUMMY_ACTION;
  }

  return _nodeHoveredActionCreatorFactory(
    components,
    focusedSymbolNativeIdPath
  );
};

const _nodeHoveredActionCreatorFactory = (
  components: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
): ActionCreator<'NODE_HOVERED', NodeHoveredPayload> => {
  return (payload: NodeHoveredPayload) => {
    const transformedPayload = transformNodeHoveredPayload(
      payload,
      components,
      focusedSymbolNativeIdPath
    );
    return createAction<'NODE_HOVERED', NodeHoveredPayload>('NODE_HOVERED')(
      transformedPayload
    );
  };
};
